import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

export interface UpdateEntry {
  key: string; // `${kind}:${packId}`
  kind: "ruleset" | "scene";
  packId: string;
  currentVersion: string;
  latestVersion: string;
}

interface Props {
  isGM: boolean;
  entries: UpdateEntry[];
  onView: (entry: UpdateEntry) => void;
  onDismiss: (entry: UpdateEntry) => Promise<void>;
}

export default function UpdateBanner({
  isGM,
  entries,
  onView,
  onDismiss,
}: Props): ReactElement | null {
  const { t } = useTranslation();
  const [dismissing, setDismissing] = useState<string | null>(null);

  if (entries.length === 0) return null;

  async function handleDismiss(entry: UpdateEntry): Promise<void> {
    setDismissing(entry.key);
    try {
      await onDismiss(entry);
    } finally {
      setDismissing(null);
    }
  }

  return (
    <aside
      className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
      data-testid="update-banner"
    >
      <div className="font-semibold">{t("update.banner.title")}</div>
      <p className="mt-1">{t("update.banner.desc", { count: entries.length })}</p>
      {!isGM && <p className="mt-1 italic opacity-80">{t("update.gm-only")}</p>}
      <ul className="mt-2 space-y-1">
        {entries.map((e) => (
          <li key={e.key} className="flex items-center gap-2">
            <span className="font-mono text-xs">
              {e.packId} {e.currentVersion} → {e.latestVersion}
            </span>
            {isGM && (
              <>
                <button
                  type="button"
                  className="rounded border border-amber-600 px-2 py-0.5 text-xs hover:bg-amber-100"
                  onClick={() => onView(e)}
                >
                  {t("update.banner.view")}
                </button>
                <button
                  type="button"
                  className="rounded border border-amber-600 px-2 py-0.5 text-xs hover:bg-amber-100 disabled:opacity-50"
                  disabled={dismissing === e.key}
                  onClick={() => handleDismiss(e)}
                >
                  {t("update.banner.dismiss")}
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
    </aside>
  );
}
